import type { AccountId, MembershipId, UserId } from "../common/ids.js";

/**
 * Ein Account ist der Mandant und der Besitzer aller Daten. Jede Abfrage im System
 * ist auf genau einen Account eingegrenzt (`account_id`-Scope, RLS-fähig).
 */
export interface Account {
  readonly id: AccountId;
  readonly name: string;
  /** Kanonischer Kurzname für URLs; eindeutig über alle Accounts. */
  readonly slug: string;
  readonly status: AccountStatus;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

export const ACCOUNT_STATUSES = ["active", "suspended", "deleted"] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

/** Höchstlänge des Slugs; entspricht der Spalte `accounts.slug`. */
export const ACCOUNT_SLUG_MAX_LENGTH = 64;

/**
 * Bildet aus einem Accountnamen den kanonischen Kurznamen.
 *
 * Umlaute werden ausgeschrieben statt entfernt: aus „Müller" wird `mueller`, nicht
 * `mller`. Das Ergebnis ist **nicht** zwangsläufig eindeutig — die Eindeutigkeit
 * stellt der Aufrufer her, indem er bei Kollision einen Zähler anhängt. Sie hier
 * zu erzwingen ginge nicht ohne Datenbankzugriff, und der gehört nicht in dieses
 * Package.
 *
 * Ein Name ganz ohne verwertbare Zeichen ergibt eine leere Zeichenkette; der
 * Aufrufer setzt dann einen Ersatz. Ein erfundener Vorgabename wäre hier eine
 * fachliche Entscheidung an der falschen Stelle.
 */
export function accountSlug(name: string): string {
  const transliterated = name
    .toLowerCase()
    .replaceAll("ä", "ae")
    .replaceAll("ö", "oe")
    .replaceAll("ü", "ue")
    .replaceAll("ß", "ss");

  return transliterated
    .normalize("NFD")
    // Alles, was nach der Zerlegung an diakritischen Zeichen übrig bleibt (é → e).
    .replaceAll(/[̀-ͯ]/gu, "")
    .replaceAll(/[^a-z0-9]+/gu, "-")
    .replaceAll(/^-+|-+$/gu, "")
    .slice(0, ACCOUNT_SLUG_MAX_LENGTH)
    .replaceAll(/-+$/gu, "");
}

/**
 * Der Name eines Accounts, der bei einer Selbstregistrierung entsteht (HM-060).
 *
 * **Der Account heißt nach seinem Besitzer.** Vorher fragte das Formular nach
 * einem „Namen des Accounts" — einer Angabe über einen Begriff dieses Systems,
 * auf die die einzige ehrliche Antwort „mein Haushalt" lautete. Wer sich
 * registriert, sagt jetzt, wie **er** heißt; der Account übernimmt das.
 *
 * **Hier und nicht im Dienst**, obwohl es eine Verkettung ist: Der Accountname
 * geht in den Slug ein, der Slug ist Teil von Adressen, und eine zweite Stelle,
 * die ihn anders bildet, erzeugte zwei Namen für denselben Account. Beide
 * Teile werden dabei getrimmt — ein nachlaufendes Leerzeichen ergäbe sonst
 * einen Namen mit doppeltem Abstand und einen Slug mit doppeltem Bindestrich.
 */
export function accountNameForPerson(firstName: string, lastName: string): string {
  return [firstName.trim(), lastName.trim()].filter((part) => part.length > 0).join(" ");
}

/**
 * Rollen innerhalb eines Accounts. `installer` ist eine zeitlich begrenzte Rolle
 * für Monteure: darf Geräte einrichten, aber keine Verbrauchshistorie einsehen.
 *
 * **`resident` ist ein Bewohner** (HM-081, ADR 0026 §3): Er darf genau eines —
 * die Werte seiner Wohneinheit lesen (`unit:read`), und auch die nur über seine
 * **Bewohnung** (`UnitOccupancy`), nicht über die Zugehörigkeit. Die Rolle
 * existiert, damit ein reiner Bewohner eine Sitzung bekommt und in der
 * Nutzerliste des Vermieters steht; was er sieht, entscheidet die Bewohnung.
 *
 * **`guest` ist ein Gast an einer geteilten Anlage** (HM-160, ADR 0043): Die
 * Rolle trägt **keine** Berechtigung. Sie existiert aus demselben Grund wie
 * `resident` — damit jemand, mit dem nur eine Anlage geteilt ist, eine Sitzung
 * bekommt. Was er an dieser Anlage darf, entscheidet die **Teilung**
 * (`SiteShare`), nicht die Rolle; an jeder anderen Anlage des Accounts darf er
 * nichts.
 */
export const ACCOUNT_ROLES = ["owner", "admin", "member", "viewer", "installer", "resident", "guest"] as const;
export type AccountRole = (typeof ACCOUNT_ROLES)[number];

/** Verknüpft einen User mit einem Account. Ein User kann in mehreren Accounts sein. */
export interface Membership {
  readonly id: MembershipId;
  readonly accountId: AccountId;
  readonly userId: UserId;
  readonly role: AccountRole;
  readonly createdAt: Date;
}

/**
 * Fachliche Berechtigungen. Bewusst grobkörnig gehalten: feinere Rechte kommen
 * erst, wenn ein konkreter Bedarf besteht — siehe TASKS.md, Backlog „Later".
 */
export const PERMISSIONS = [
  "account:manage",
  "member:manage",
  "site:read",
  "site:write",
  "device:read",
  "device:provision",
  "meter:read",
  "meter:write",
  "series:read",
  "concept:read",
  "concept:write",
  /** Die Werte einer Wohneinheit lesen — über die Bewohnung oder als Besitzer (HM-081). */
  "unit:read",
  /**
   * Löschen, Archivieren, Freigeben (HM-160): Anlage oder Zähler archivieren,
   * ein Gerät freigeben, eine Wohneinheit oder Erzeugungsanlage löschen. Eigens,
   * weil eine Teilung zum Schreiben alles darf **außer** diesem — vorher hing
   * es an der jeweiligen `:write`-Berechtigung.
   */
  "data:delete",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const ROLE_PERMISSIONS: Readonly<Record<AccountRole, readonly Permission[]>> = {
  owner: [...PERMISSIONS],
  admin: PERMISSIONS.filter((p) => p !== "account:manage"),
  member: [
    "site:read",
    "site:write",
    "device:read",
    "device:provision",
    "meter:read",
    "meter:write",
    "series:read",
    "concept:read",
    "concept:write",
    "unit:read",
    "data:delete",
  ],
  viewer: ["site:read", "device:read", "meter:read", "series:read", "concept:read", "unit:read"],
  // Der Monteur richtet ein, sieht aber keine Verbrauchsdaten — auch nicht je Wohnung.
  installer: ["site:read", "device:read", "device:provision", "meter:read", "meter:write"],
  // Der Bewohner sieht seine Wohnung, sonst nichts. Kein `site:read`: Die Anlage
  // gehört dem Vermieter, und ihre Liste enthielte die anderen Wohnungen.
  resident: ["unit:read"],
  // Der Gast darf über die Zugehörigkeit nichts; die Teilung an der Anlage
  // entscheidet (`shareAccessAllows`).
  guest: [],
};

export function permissionsOf(role: AccountRole): readonly Permission[] {
  return ROLE_PERMISSIONS[role];
}

export function hasPermission(role: AccountRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}
